/**
 * Permission and entitlement decisions for the UI (pure). The UI mirrors
 * the server and fails closed (BUILD-PLAN 2.6): unknown means "no". The
 * server stays the authority; hiding a button is a courtesy, not security.
 */
import type { EntitlementState } from '@velora/contracts';
import type { ShellBootstrap } from '@velora/contracts/shell';

type Access = Pick<ShellBootstrap, 'permissions' | 'locationPermissions'>;

/** Does the user hold `key` at `locationId` (or practice-wide when null)? */
export function can(access: Access | null, key: string, locationId: string | null): boolean {
  if (!access) return false;
  if (locationId !== null) {
    const override = access.locationPermissions[locationId];
    if (override) return override.includes(key);
  }
  return access.permissions.includes(key);
}

export type PermissionMode = 'any' | 'every';

/**
 * Permission across the offices in scope. `any` (default) mirrors the
 * server, which narrows a multi-office request to the offices where the
 * user holds the permission; `every` is for actions that must apply to all.
 */
export function canInScope(
  access: Access | null,
  key: string,
  locationIds: readonly string[],
  mode: PermissionMode = 'any',
): boolean {
  if (!access) return false;
  if (locationIds.length === 0) return can(access, key, null);
  return mode === 'every'
    ? locationIds.every((id) => can(access, key, id))
    : locationIds.some((id) => can(access, key, id));
}

export interface EntitlementView {
  /** `mixed` when offices in scope differ. */
  state: EntitlementState | 'mixed';
  perOffice: Readonly<Record<string, EntitlementState>>;
  /** At least one office in scope can use it fully. */
  active: boolean;
  /** Reads allowed somewhere in scope (active or read-only grace). */
  readable: boolean;
  /** A 14-day trial can be started for at least one office. */
  trialAvailable: boolean;
}

/** Core modules (`featureKey === null`) are always active. */
export function entitlementFor(
  boot: Pick<ShellBootstrap, 'entitlements' | 'trialsAvailable'> | null,
  featureKey: string | null,
  locationIds: readonly string[],
): EntitlementView {
  if (featureKey === null) {
    return { state: 'active', perOffice: {}, active: true, readable: true, trialAvailable: false };
  }
  const table = boot?.entitlements[featureKey] ?? {};
  const perOffice: Record<string, EntitlementState> = {};
  for (const id of locationIds) perOffice[id] = table[id] ?? 'none';
  const states = new Set(Object.values(perOffice));
  const state: EntitlementView['state'] =
    states.size === 0 ? 'none' : states.size === 1 ? ([...states][0] as EntitlementState) : 'mixed';
  return {
    state,
    perOffice,
    active: states.has('active'),
    readable: states.has('active') || states.has('read_only'),
    trialAvailable: boot?.trialsAvailable.includes(featureKey) ?? false,
  };
}
